import * as React from 'react'
import { ChevronRight, type LucideIcon } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import type { StaffModule } from '@/pages/staffCapabilities'
import markUrl from '@/assets/bright-huddle/mark.svg'
import lockupUrl from '@/assets/bright-huddle/option-c-gather.svg'

export interface NavSubItem {
  label: string
  active?: boolean
  href?: string
  onSelect?: () => void
  module?: StaffModule
}

export interface NavItem {
  label: string
  icon: LucideIcon
  active?: boolean
  href?: string
  onSelect?: () => void
  /** Optional sub-pages — when present, the item renders as a collapsible section. */
  children?: NavSubItem[]
}

export interface AppShellProps {
  /** The Association currently being operated (shown in the rail header). */
  associationName: string
  nav: NavItem[]
  user?: { name: string; onSignOut?: () => void; signingOut?: boolean }
  viewOnly?: boolean
  showViewOnlyBadge?: boolean
  defaultSidebarOpen?: boolean
  children: React.ReactNode
}

export const ViewOnlyContext = React.createContext(false)
const ViewOnlyBadgeContext = React.createContext(false)

export function useViewOnly() {
  return React.useContext(ViewOnlyContext)
}

export function ViewOnlyBadge() {
  return useViewOnlyBadge() ? <Badge variant="info">View only</Badge> : null
}

export function useViewOnlyBadge() {
  return React.useContext(ViewOnlyBadgeContext)
}

function sectionActive(item: NavItem) {
  return Boolean(item.active || item.children?.some((c) => c.active))
}

function activeSectionLabel(nav: NavItem[]) {
  return nav.find((item) => item.children?.length && sectionActive(item))?.label ?? null
}

/**
 * One nav section open at a time. The section holding the current page opens
 * by default and again whenever the current page moves to another section.
 */
function useAccordionSection(nav: NavItem[]) {
  const active = activeSectionLabel(nav)
  const [state, setState] = React.useState({ active, open: active })
  if (state.active !== active) setState({ active, open: active })
  const open = state.active === active ? state.open : active
  const setOpen = React.useCallback(
    (label: string | null) => setState((current) => ({ ...current, open: label })),
    [],
  )
  return [open, setOpen] as const
}

function selectNav(event: React.MouseEvent<HTMLAnchorElement>, onSelect?: () => void) {
  if (!onSelect) return

  event.preventDefault()
  onSelect()
}

/**
 * The persistent application frame, built on the shadcn Sidebar primitive in
 * its sidebar-07 arrangement: a left rail that collapses to icons (Cmd+B or
 * the rail edge), a sheet drawer on mobile, content to the right. Nav items
 * may carry sub-items, which render as an accordion of collapsible sections:
 * one open at a time, the active section expanded by default.
 */
export function AppShell({
  associationName,
  nav,
  user,
  viewOnly = false,
  showViewOnlyBadge = viewOnly,
  defaultSidebarOpen = true,
  children,
}: AppShellProps) {
  const [openSection, setOpenSection] = useAccordionSection(nav)
  return (
    <SidebarProvider defaultOpen={defaultSidebarOpen}>
      <Sidebar collapsible="icon">
        <SidebarHeader role="region" aria-label="Association identity">
          <div className="flex items-center px-2 py-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-1">
            <img
              src={lockupUrl}
              alt=""
              aria-hidden="true"
              className="h-auto w-52 max-w-full group-data-[collapsible=icon]:hidden"
            />
            <img
              src={markUrl}
              alt=""
              aria-hidden="true"
              className="hidden size-8 shrink-0 group-data-[collapsible=icon]:block"
            />
            <span className="sr-only">{associationName}</span>
          </div>
        </SidebarHeader>
        <SidebarContent role="navigation" aria-label="Staff navigation">
          <SidebarMenu className="px-2 py-1">
            {nav.map((item) => {
              if (!item.children?.length) {
                return (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton asChild isActive={item.active} tooltip={item.label}>
                      <a
                        href={item.href ?? '#'}
                        aria-current={item.active ? 'page' : undefined}
                        onClick={(event) => selectNav(event, item.onSelect)}
                      >
                        <item.icon strokeWidth={1.5} />
                        <span>{item.label}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              }

              return (
                <Collapsible
                  key={item.label}
                  asChild
                  open={openSection === item.label}
                  onOpenChange={(open) => setOpenSection(open ? item.label : null)}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.label}>
                        <item.icon strokeWidth={1.5} />
                        <span>{item.label}</span>
                        <ChevronRight
                          className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                          strokeWidth={1.5}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.children.map((sub) => (
                          <SidebarMenuSubItem key={sub.label}>
                            <SidebarMenuSubButton asChild isActive={sub.active}>
                              <a
                                href={sub.href ?? '#'}
                                aria-current={sub.active ? 'page' : undefined}
                                onClick={(event) => selectNav(event, sub.onSelect)}
                              >
                                <span>{sub.label}</span>
                              </a>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )
            })}
          </SidebarMenu>
        </SidebarContent>
        {user && <StaffSidebarFooter user={user} />}
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="min-w-0 md:h-svh">
        <header className="flex h-12 shrink-0 items-center border-b border-border px-4 md:hidden">
          <SidebarTrigger />
        </header>
        <ViewOnlyContext.Provider value={viewOnly}>
          <ViewOnlyBadgeContext.Provider value={showViewOnlyBadge}>
            <div className="min-w-0 flex-1 overflow-auto">{children}</div>
          </ViewOnlyBadgeContext.Provider>
        </ViewOnlyContext.Provider>
      </SidebarInset>
    </SidebarProvider>
  )
}

function StaffSidebarFooter({ user }: { user: NonNullable<AppShellProps['user']> }) {
  return (
    <SidebarFooter
      role="region"
      aria-label="Staff account"
      className="border-t border-sidebar-border"
    >
      <div className="flex items-center gap-2.5 px-2 py-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
        <Avatar className="size-8">
          <AvatarFallback>{user.name.slice(0, 1)}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1 truncate text-body-sm text-sidebar-foreground group-data-[collapsible=icon]:hidden">
          {user.name}
        </span>
        {user.onSignOut && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={user.signingOut}
            onClick={user.onSignOut}
            className="group-data-[collapsible=icon]:hidden"
          >
            {user.signingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        )}
      </div>
    </SidebarFooter>
  )
}
